import { jwtDecode } from 'jwt-decode';

import { getEnvVar } from '@/env';
import { readEmailFromToken } from '@/features/login/token-email';
import { clearUserSession } from '@/lib/auth/auth-listeners';
import {
  ACCESS_TOKEN_LOCAL_STORAGE_KEY,
  REFRESH_TOKEN_LOCAL_STORAGE_KEY,
} from '@/utils/constants';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

type ChannelSignInFetcher = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

interface ChannelSignInDependencies {
  readonly fetcher?: ChannelSignInFetcher;
  readonly getAccessToken?: () => unknown;
  readonly setAccessToken?: (token: string) => void;
  readonly removeRefreshToken?: () => void;
  readonly clearUserState?: () => Promise<void>;
}

function tokenExpiresMoreThanMinuteFromNow(token: string): boolean {
  try {
    const expiresAt = jwtDecode<{ exp?: number }>(token).exp;
    return (
      typeof expiresAt === 'number' && expiresAt * 1000 > Date.now() + 60_000
    );
  } catch {
    return false;
  }
}

const CHANNEL_SIGN_IN_FALLBACK = '/channels/integrations';
const WEB_SIGN_IN_CODE_MAX_LENGTH = 64;

function isValidCode(code: unknown): code is string {
  return (
    typeof code === 'string' &&
    code.length > 0 &&
    code.length <= WEB_SIGN_IN_CODE_MAX_LENGTH
  );
}

function currentAccessToken(dependencies: ChannelSignInDependencies) {
  return dependencies.getAccessToken
    ? dependencies.getAccessToken()
    : getLocalStorageItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
}

export function freshChannelSessionEmail(
  dependencies: ChannelSignInDependencies = {}
): string | null {
  const token = currentAccessToken(dependencies);
  if (typeof token !== 'string' || !tokenExpiresMoreThanMinuteFromNow(token)) {
    return null;
  }
  return readEmailFromToken(token);
}

export function safeChannelSignInNext(next: unknown): string {
  if (typeof next !== 'string' || !next.startsWith('/')) {
    return CHANNEL_SIGN_IN_FALLBACK;
  }
  try {
    const url = new URL(next, 'https://app.littlebird.ai');
    if (
      url.origin !== 'https://app.littlebird.ai' ||
      (url.pathname !== '/channels' && !url.pathname.startsWith('/channels/'))
    ) {
      return CHANNEL_SIGN_IN_FALLBACK;
    }
    url.searchParams.delete('lb_sign_in');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return CHANNEL_SIGN_IN_FALLBACK;
  }
}

export async function peekChannelSignIn(
  code: unknown,
  dependencies: ChannelSignInDependencies = {}
): Promise<string | null> {
  if (!isValidCode(code)) return null;
  const fetcher = dependencies.fetcher ?? fetch;

  try {
    const response = await fetcher(
      `${getEnvVar('VITE_API_URL')}/msg-channels/web-sign-in/peek`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      }
    );
    if (!response.ok) return null;
    const result: unknown = await response.json();
    return typeof result === 'object' &&
      result !== null &&
      'email' in result &&
      typeof result.email === 'string'
      ? result.email
      : null;
  } catch {
    return null;
  }
}

export type ChannelSignInDecision =
  | { readonly kind: 'expired' }
  | { readonly kind: 'continue' }
  | {
      readonly kind: 'confirm';
      readonly code: string;
      readonly next: string;
    };

export async function resolveChannelSignIn(
  code: unknown,
  next: string,
  dependencies: ChannelSignInDependencies = {}
): Promise<ChannelSignInDecision> {
  if (!isValidCode(code)) return { kind: 'expired' };
  const email = await peekChannelSignIn(code, dependencies);
  if (email === null) return { kind: 'expired' };
  if (freshChannelSessionEmail(dependencies) === email) {
    return { kind: 'continue' };
  }
  return {
    kind: 'confirm',
    code,
    next: safeChannelSignInNext(next),
  };
}

export async function redeemChannelSignIn(
  code: string,
  dependencies: ChannelSignInDependencies = {}
): Promise<boolean> {
  if (!isValidCode(code)) return false;
  const fetcher = dependencies.fetcher ?? fetch;
  const getAccessToken =
    dependencies.getAccessToken ??
    (() => getLocalStorageItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY));
  const setAccessToken =
    dependencies.setAccessToken ??
    ((token: string) =>
      setLocalStorageItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, token));
  const removeRefreshToken =
    dependencies.removeRefreshToken ??
    (() => localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY));
  const clearUserState = dependencies.clearUserState ?? clearUserSession;

  try {
    const response = await fetcher(
      `${getEnvVar('VITE_API_URL')}/msg-channels/web-sign-in`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      }
    );
    if (!response.ok) return false;
    const result: unknown = await response.json();
    if (
      typeof result !== 'object' ||
      result === null ||
      !('access_token' in result) ||
      typeof result.access_token !== 'string'
    ) {
      return false;
    }

    const nextToken = result.access_token;
    const nextEmail = readEmailFromToken(nextToken);
    if (nextEmail === null) return false;
    const currentToken = getAccessToken();
    const currentEmail =
      typeof currentToken === 'string'
        ? readEmailFromToken(currentToken)
        : null;
    const tokenIsFresh =
      typeof currentToken === 'string' &&
      tokenExpiresMoreThanMinuteFromNow(currentToken);
    if (currentEmail === nextEmail && tokenIsFresh) return true;

    await clearUserState();
    removeRefreshToken();
    setAccessToken(nextToken);
    return true;
  } catch {
    return false;
  }
}
